// provisional-component.ts — provisional print / cut / lamination / finishing created during a quote.
//
// Same lifecycle as provisional materials: ref suffixed -NEW, reserved atomically, usable only in the
// originating quote. Pricing uses the entered PV €/m² (not tier-1 margin derivation).

import type { Cut, Finishing, Lamination, Print } from "../../models/data.model";
import type { Catalogue } from "./catalogue-source";
import { COMPONENT_AXIS, M2_RANGE, nextRef, type ComponentKind } from "./reference-creation";
import { isProvisionalRef, PROVISIONAL_SUFFIX, finalRef } from "./provisional-material";
import { round2 } from "../core/core";

export type ProvisionalComponentType = ComponentKind;

/** Provisional component row (panel + database). */
export interface ProvisionalComponent {
  ref: string;
  type: ProvisionalComponentType;
  itemShort: string;
  paM2: number;
  pvM2: number;
  status: "provisional" | "active";
}

/** Panel draft. */
export interface ProvisionalComponentDraft {
  itemShort: string;
  paM2: number | string | null;
  pvM2: number | string | null;
}

/** Validation result. */
export interface ProvisionalComponentResult {
  blockers: string[];
  creatable: boolean;
  normalized: Omit<ProvisionalComponent, "ref" | "status"> | null;
}

/**
 * Strict number parse for a typed price: « 12 », « 12.5 » and « 12,5 » only. `num()` turns anything non numeric into
 * 0, which would create a component priced at 0; « abc » and « 12foo » are refused here.
 * @param v Typed value
 * @returns The number, or null when empty or not a plain number
 */
function strictNumber(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const text = String(v).trim().replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

/**
 * Validates the slim provisional-component panel.
 * @param type Component kind
 * @param b Draft fields
 * @returns Blockers and normalized payload
 */
export function validateProvisionalComponent(
  type: ProvisionalComponentType,
  b: ProvisionalComponentDraft,
): ProvisionalComponentResult {
  const blockers: string[] = [];
  const itemShort = (b.itemShort ?? "").trim();
  if (!itemShort) blockers.push("engineProvCompItemShortRequired");
  const pa = strictNumber(b.paM2);
  if (pa == null || pa < 0) blockers.push("engineProvCompPaRequired");
  const pv = strictNumber(b.pvM2);
  if (pv == null || pv < 0) blockers.push("engineProvCompPvRequired");
  if (blockers.length) return { blockers, creatable: false, normalized: null };
  return {
    blockers: [],
    creatable: true,
    normalized: { type, itemShort, paM2: round2(pa!), pvM2: round2(pv!) },
  };
}

/**
 * Expected provisional ref for a component type (indicative; DB reservation is authoritative).
 * @param type Component kind
 * @param existingRefs Catalogue + reserved roots
 * @returns Expected ref or null
 */
export function expectedProvisionalComponentRef(type: ProvisionalComponentType, existingRefs: readonly string[]): string | null {
  const pfx = COMPONENT_AXIS[type]?.prefix;
  if (!pfx) return null;
  const roots = existingRefs.map((r) => finalRef(r));
  return nextRef(pfx, roots, M2_RANGE) + PROVISIONAL_SUFFIX;
}

function toPrint(p: ProvisionalComponent): Print {
  return { code: p.ref, label: p.itemShort, pv: p.pvM2, min_unit_price: 0, active: true };
}

function toCut(p: ProvisionalComponent): Cut {
  return { code: p.ref, label: p.itemShort, pv: p.pvM2, min_unit_price: 0, trigger: null, active: true };
}

function toLamination(p: ProvisionalComponent): Lamination {
  return { code: p.ref, label: p.itemShort, pv: p.pvM2, min_unit_price: 0, active: true };
}

function toFinishing(p: ProvisionalComponent): Finishing {
  return { code: p.ref, label: p.itemShort, pv: p.pvM2, active: true };
}

/**
 * Merges live provisional components into the calculation catalogue of the originating quote.
 * @param base Base catalogue
 * @param provisionals Provisional components of the quote
 * @returns Catalogue with injected components
 */
export function catalogueWithComponentProvisionals(
  base: Catalogue,
  provisionals: readonly ProvisionalComponent[],
): Catalogue {
  const live = provisionals.filter((p) => p.status === "provisional");
  if (!live.length) return base;
  const prints = [...base.prints];
  const cuts = [...base.cuts];
  const laminations = [...base.laminations];
  const finishings = [...base.finishings];
  for (const p of live) {
    switch (p.type) {
      case "print":
        if (!prints.some((x) => x.code === p.ref)) prints.push(toPrint(p));
        break;
      case "cut":
        if (!cuts.some((x) => x.code === p.ref)) cuts.push(toCut(p));
        break;
      case "lamination":
        if (!laminations.some((x) => x.code === p.ref)) laminations.push(toLamination(p));
        break;
      case "finishing":
        if (!finishings.some((x) => x.code === p.ref)) finishings.push(toFinishing(p));
        break;
    }
  }
  return { ...base, prints, cuts, laminations, finishings };
}
